import { useState } from "react";
import { Users, Pencil, Trash2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { formatDate } from "@/lib/formatters";
import { getDriverDisplayName } from "@/lib/driver-display";
import type { Policy, PolicyDriver } from "@shared/schema";
import { AssetSectionHeader } from "./AssetSectionHeader";
import { AssetStatusBadge, statusForAsset } from "./AssetStatusBadge";
import type { DriverEntry } from "./asset-helpers";
import { DriverDialog } from "./DriverDialog";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { useUpdatePolicyDrivers } from "@/lib/api";

interface Props {
  drivers: DriverEntry[];
  policies: Policy[];
}

const HEAD_CLASS = "px-6 py-3 text-xs uppercase tracking-wider text-muted-foreground";
const CELL_CLASS = "px-6 py-5";

export function DriversSection({
  drivers,
  policies,
}: Props) {
  const { toast } = useToast();
  const { confirm, confirmDialog } = useConfirm();
  const updateDrivers = useUpdatePolicyDrivers();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editData, setEditData] = useState<PolicyDriver | null>(null);
  const [editPolicyId, setEditPolicyId] = useState<string | null>(null);

  function handleAdd() {
    setEditData(null);
    setEditPolicyId(null);
    setDialogOpen(true);
  }

  function handleEdit(entry: DriverEntry) {
    setEditData(entry.driver);
    setEditPolicyId(entry.policyId);
    setDialogOpen(true);
  }

  async function handleRemove(entry: DriverEntry) {
    const ok = await confirm({
      title: "Remove Driver",
      description: `Remove driver ${entry.driver.name ?? "this driver"} from the policy?`,
      confirmLabel: "Remove",
      destructive: true,
    });
    if (!ok) return;
    const policy = policies.find((p) => p.id === entry.policyId);
    if (!policy) return;
    const current = (policy.drivers as PolicyDriver[] | null) ?? [];
    // CSR-1345 — drop every variant row of this display entry on this policy,
    // by reference (aggregateDrivers carries the underlying rows), so a hidden
    // masked twin cannot resurface after the remove. The previous name+addedAt
    // filter also over-matched: nameless rows sharing an addedAt were all
    // deleted at once (prod dc-17796).
    const variantRows = (entry.sourceRows ?? [])
      .filter((s) => s.policyId === policy.id)
      .map((s) => s.driver)
      .filter((d) => current.includes(d));
    let next: PolicyDriver[];
    if (variantRows.length > 0) {
      next = current.filter((d) => !variantRows.includes(d));
    } else {
      // Fallback for entries without source references: remove exactly one row.
      const index = current.findIndex(
        (d) => d.name === entry.driver.name && d.addedAt === entry.driver.addedAt,
      );
      if (index < 0) return;
      next = current.filter((_, i) => i !== index);
    }
    try {
      await updateDrivers.mutateAsync({ id: policy.id, drivers: next });
      toast({ title: "Removed" });
    } catch (err) {
      toast({
        title: "Failed to remove",
        description: err instanceof Error ? err.message : undefined,
        variant: "destructive",
      });
    }
  }

  return (
    <Card className="overflow-hidden" data-testid="drivers-section">
      <AssetSectionHeader
        icon={Users}
        title="Drivers"
        count={drivers.length}
        testId="drivers"
        onAdd={policies.length > 0 ? handleAdd : undefined}
        disabledTooltip={policies.length === 0 ? "This insured has no policy yet — assets are stored on a policy, so add one first" : undefined}
      />
      {drivers.length === 0 ? (
        <div
          className="px-6 py-12 text-center text-sm text-muted-foreground"
          data-testid="drivers-empty"
        >
          No drivers on record.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className={HEAD_CLASS}>Name</TableHead>
                <TableHead className={HEAD_CLASS}>DL Number</TableHead>
                <TableHead className={HEAD_CLASS}>DL State</TableHead>
                <TableHead className={HEAD_CLASS}>Date of Birth</TableHead>
                <TableHead className={HEAD_CLASS}>Hire Date</TableHead>
                <TableHead className={HEAD_CLASS}>Years of Experience</TableHead>
                <TableHead className={HEAD_CLASS}>Status</TableHead>
                <TableHead className={HEAD_CLASS}></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {drivers.map((entry, i) => {
                const testIdSuffix = entry.driver.name ?? `driver-${i}`;
                return (
                  <TableRow
                    key={entry.driver.licenseNumber ?? `driver-${i}`}
                    data-testid={`driver-row-${entry.driver.licenseNumber ?? i}`}
                  >
                    <TableCell className={`${CELL_CLASS} font-medium`}>
                      {getDriverDisplayName(entry.driver)}
                    </TableCell>
                    <TableCell className={`${CELL_CLASS} font-mono text-sm text-muted-foreground`}>
                      {entry.driver.licenseNumber || "—"}
                    </TableCell>
                    <TableCell className={`${CELL_CLASS} text-muted-foreground`}>
                      {entry.driver.licenseState || "—"}
                    </TableCell>
                    <TableCell
                      className={`${CELL_CLASS} text-muted-foreground`}
                      data-testid={`driver-dob-${entry.driver.licenseNumber ?? i}`}
                    >
                      {entry.driver.dateOfBirth ? formatDate(entry.driver.dateOfBirth, { dateOnly: true }) : "—"}
                    </TableCell>
                    <TableCell className={`${CELL_CLASS} text-muted-foreground`}>
                      {entry.driver.dateOfHire ? formatDate(entry.driver.dateOfHire, { dateOnly: true }) : "—"}
                    </TableCell>
                    <TableCell
                      className={`${CELL_CLASS} text-muted-foreground`}
                      data-testid={`driver-yoe-${entry.driver.licenseNumber ?? i}`}
                    >
                      {entry.driver.yearsExperience != null
                        ? `${entry.driver.yearsExperience} yrs`
                        : "—"}
                    </TableCell>
                    <TableCell className={CELL_CLASS}>
                      <AssetStatusBadge status={statusForAsset(entry.removedAt, entry.active)} />
                    </TableCell>
                    <TableCell className={CELL_CLASS}>
                      <div
                        className="flex items-center gap-1"
                        data-testid={`driver-row-action-${testIdSuffix}`}
                      >
                        <Button
                          size="sm"
                          variant="ghost"
                          className="cursor-pointer"
                          aria-label={`Edit driver ${entry.driver.name ?? ""}`}
                          disabled={updateDrivers.isPending}
                          onClick={() => handleEdit(entry)}
                          data-testid={`driver-edit-${testIdSuffix}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="cursor-pointer text-destructive"
                          aria-label={`Remove driver ${entry.driver.name ?? ""}`}
                          disabled={updateDrivers.isPending}
                          onClick={() => handleRemove(entry)}
                          data-testid={`driver-remove-${testIdSuffix}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <DriverDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        policies={policies}
        editData={editData}
        editPolicyId={editPolicyId}
      />
      {confirmDialog}
    </Card>
  );
}
